/**
 * The repo's organization binding — one repo, one org, inherited by every
 * worktree of it. Hubs and bases self-identify inside their own folders; this
 * carries only the org.
 *
 * It lives in the workspace manifest, `wayai-ws/wayai.yaml`
 * (`workspace-manifest.ts`), beside the routing defaults. The root `.wayai.yaml`
 * that used to hold it is a **deprecated fallback**: read when the manifest
 * declares no org, with a one-time notice pointing at `wayai migrate`.
 *
 * **The two files disagreeing is REFUSED, never resolved by precedence.** Every
 * request the CLI makes is org-scoped, and base ids are org-scoped slugs, so
 * quietly preferring one file would push a teammate's config into an
 * organization they never named — and report success. A refusal names both ids
 * and both files and leaves the choice with the user.
 *
 * The same rule is why a manifest that fails to parse does not fall through to
 * the root file: we cannot know whether it declared an org, so we cannot know
 * the fallback agrees with it.
 *
 * **Deprecation horizon.** `wayai migrate` COPIES the org into the manifest and
 * leaves the root file in place, because a teammate or a CI runner still on an
 * older CLI requires it. Removing this fallback and the file itself is a later
 * major, once every environment that runs a given repo reads the manifest.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { expected } from './expected.js';
import { readRealFileOrThrow, requireRealSubdirNoSymlink, writeFileNoFollow } from './fs-safety.js';
import { findGitRoot } from './workspace.js';
import { UUID_RE } from './utils.js';
import {
  ROOT_CONFIG_FILE,
  WORKSPACE_MANIFEST_LABEL,
  loadWorkspaceManifest,
  loadYamlMapping,
  rootConfigPath,
  workspaceManifestPath,
  type YamlMappingLoad,
} from './workspace-manifest.js';

export interface RepoConfig {
  organization_id: string;
  organization_name?: string;
}

/** Which file supplied the organization. */
export type OrgSource = 'manifest' | 'root';

export interface ResolvedRepoConfig {
  config: RepoConfig;
  source: OrgSource;
  /** Absolute path to the file that answered — what `wayai status` reports. */
  path: string;
}

const LEGACY_FIELDS = ['project_id', 'project_name', 'hub_id', 'hub_name'] as const;

/**
 * One-time notices, keyed by the CONDITION rather than by a path, so the set
 * stays bounded however many repos one process touches. A command that reads the
 * config twice must not scold the user twice — the same job `_resetLayoutWarning`
 * does for `layout.ts`, generalized to more than one message.
 *
 * Deduplication is the SOFT posture's alone. `requireRepoConfig` exits right
 * after printing, so suppressing a repeat there would mean exiting non-zero with
 * nothing on stderr at all; `report` takes the posture for exactly that reason.
 */
type NoticeKey = 'legacy-fields' | 'root-fallback' | `invalid:${OrgSource}`;

const noticed = new Set<NoticeKey>();

function noticeOnce(key: NoticeKey, emit: () => void): void {
  if (noticed.has(key)) return;
  noticed.add(key);
  emit();
}

/** Test-only: forget which one-time notices this process has printed. */
export function _resetRepoConfigNotices(): void {
  noticed.clear();
}

/** What one file says about the organization, before resolving between files. */
type OrgDeclaration =
  | { kind: 'none' }
  | { kind: 'invalid'; detail: string }
  | { kind: 'declared'; config: RepoConfig };

/**
 * Pull the org fields out of an already-parsed document.
 *
 * A present-but-unusable `organization_id` is `invalid`, never `none`: the
 * difference decides whether the deprecated root file gets consulted, and
 * treating a typo'd UUID as "declares nothing" would silently bind whatever the
 * other file happens to say.
 */
function readOrgFields(doc: Record<string, unknown>): OrgDeclaration {
  const raw = doc.organization_id;
  if (raw === undefined || raw === null) {
    // The pre-UUID spelling: `organization: <name>`.
    if (typeof doc.organization === 'string') {
      return {
        kind: 'invalid',
        detail: 'it uses the old format (organization: <name>) — replace it with organization_id: <uuid>',
      };
    }
    return { kind: 'none' };
  }
  if (typeof raw !== 'string' || !UUID_RE.test(raw.trim())) {
    return { kind: 'invalid', detail: 'organization_id must be a UUID' };
  }

  const name =
    typeof doc.organization_name === 'string' && doc.organization_name.trim()
      ? doc.organization_name.trim()
      : undefined;
  const config: RepoConfig = { organization_id: raw.trim() };
  if (name) config.organization_name = name;
  return { kind: 'declared', config };
}

/** One file's load, as an org declaration. Both files map the same way. */
function declarationFrom(load: YamlMappingLoad): OrgDeclaration {
  if (load.kind === 'absent') return { kind: 'none' };
  if (load.kind === 'malformed') return { kind: 'invalid', detail: `it does not parse (${load.reason})` };
  return readOrgFields(load.doc);
}

/**
 * Legacy fields (`project_id`, `hub_id`, …) present in the root file. They only
 * ever existed there, so nothing looks for them in the manifest. Detected here,
 * ANNOUNCED by `report` and only when the root file is the source: telling a
 * migrated repo to tidy fields inside a file the CLI has already stopped reading
 * points at the wrong cleanup.
 */
function legacyFieldsIn(load: YamlMappingLoad): string[] {
  if (load.kind !== 'ok') return [];
  return LEGACY_FIELDS.filter((f) => load.doc[f] !== undefined);
}

type Resolution =
  | { kind: 'ok'; resolved: ResolvedRepoConfig; legacyRootFields: string[] }
  | { kind: 'none' }
  | { kind: 'invalid'; source: OrgSource; file: string; detail: string }
  | { kind: 'conflict'; manifestOrg: string; rootOrg: string };

/**
 * Manifest first, deprecated root second, refusal when they disagree.
 *
 * Pure: it reads files and returns a verdict, printing nothing. `report` below
 * owns what the user is told, so the soft and hard callers cannot drift into
 * saying different things about the same state.
 */
function resolve(gitRoot: string): Resolution {
  const manifestFile = workspaceManifestPath(gitRoot);
  const manifest = declarationFrom(loadWorkspaceManifest(gitRoot));

  // Returns before the root file is even read: a manifest we could not use says
  // nothing about whether it declared an org, so no fallback can be known to
  // agree with it.
  if (manifest.kind === 'invalid') {
    return { kind: 'invalid', source: 'manifest', file: manifestFile, detail: manifest.detail };
  }

  const rootFile = rootConfigPath(gitRoot);
  const rootLoad = loadYamlMapping(rootFile);
  const root = declarationFrom(rootLoad);

  if (manifest.kind === 'declared') {
    // Only a VALID root declaration can disagree — a broken deprecated file
    // declares nothing, and the manifest is authoritative regardless.
    if (
      root.kind === 'declared' &&
      root.config.organization_id !== manifest.config.organization_id
    ) {
      return {
        kind: 'conflict',
        manifestOrg: manifest.config.organization_id,
        rootOrg: root.config.organization_id,
      };
    }
    return {
      kind: 'ok',
      resolved: { config: manifest.config, source: 'manifest', path: manifestFile },
      legacyRootFields: [],
    };
  }

  if (root.kind === 'declared') {
    return {
      kind: 'ok',
      resolved: { config: root.config, source: 'root', path: rootFile },
      legacyRootFields: legacyFieldsIn(rootLoad),
    };
  }
  if (root.kind === 'invalid') {
    return { kind: 'invalid', source: 'root', file: rootFile, detail: root.detail };
  }
  return { kind: 'none' };
}

/** The remedy every disagreement refusal ends with, save `wayai migrate`'s. */
const RECONCILE_REMEDY = [
  'Keep the organization_id you actually work in and delete the other declaration.',
  `\`wayai init\` rewrites ${WORKSPACE_MANIFEST_LABEL}; the root file is yours to remove.`,
];

/**
 * The disagreement refusal. ONE text — `wayai migrate` prints it too, differing
 * only in the remedy it can offer, and two copies of a refusal drift the moment
 * either wording changes.
 */
function conflictMessage(
  manifestOrg: string,
  rootOrg: string,
  remedy: string[] = RECONCILE_REMEDY,
): string {
  return [
    'Refusing to guess the organization: two files in this repo name different ones.',
    `  ${WORKSPACE_MANIFEST_LABEL} -> ${manifestOrg}   (the workspace manifest)`,
    `  ${ROOT_CONFIG_FILE} -> ${rootOrg}   (deprecated fallback)`,
    '',
    ...remedy,
  ].join('\n');
}

/**
 * The re-bind refusal — a different question from `conflictMessage`'s.
 *
 * That one reports what the two files SAY and is only reachable once both hold
 * an organization. This one fires BEFORE anything is written, so the manifest
 * holds neither id: one is what the caller just asked for and the other is what
 * the deprecated root file still says. Rendering that through `conflictMessage`
 * printed `wayai-ws/wayai.yaml -> <requested>` about a file that may not exist
 * and has never named it — and on a repo where the manifest DOES hold a third
 * org, two refusal blocks claiming different contents for the same file.
 */
function rebindRefusal(requestedOrg: string, rootOrg: string): string {
  return [
    `Refusing to re-bind the organization: ${ROOT_CONFIG_FILE} still names a different one, and it is read as a fallback.`,
    `  requested        -> ${requestedOrg}`,
    `  ${ROOT_CONFIG_FILE}      -> ${rootOrg}   (deprecated fallback)`,
    '',
    `Delete ${ROOT_CONFIG_FILE} (or set its organization_id to ${requestedOrg}), then re-run.`,
    'Nothing was written — this repo still resolves to the organization it had.',
  ].join('\n');
}

function invalidMessage(file: string, detail: string): string {
  return [
    `Invalid organization binding in ${file}: ${detail}.`,
    '',
    '  organization_id: <your-org-uuid>',
    '',
    'Run `wayai init` to set up the workspace.',
  ].join('\n');
}

function missingMessage(): string {
  return [
    'This repository is not scoped to an organization.',
    `Declare it in ${WORKSPACE_MANIFEST_LABEL}:`,
    '',
    '  organization_id: <your-org-uuid>',
    '',
    'Run `wayai init` to set up the workspace.',
  ].join('\n');
}

/**
 * Everything the user is told about a resolution, in one place.
 *
 * `soft` serves the callers an org is optional context for: an unusable binding
 * is announced once, and "no binding at all" is the ordinary state, so it says
 * nothing. `hard` serves `requireRepoConfig`, which exits immediately after — so
 * it never deduplicates, and it does speak up about a missing binding.
 *
 * The disagreement refusal is printed on BOTH postures and deduplicated on
 * neither: a soft caller that returned null in silence would leave the command
 * carrying on as though the repo were merely unscoped, which is the guess this
 * whole rule exists to refuse.
 */
function report(r: Resolution, posture: 'soft' | 'hard'): void {
  switch (r.kind) {
    case 'ok':
      if (r.resolved.source !== 'root') return;
      noticeOnce('root-fallback', () =>
        console.warn(
          `Note: this repo's organization binding is still in ${ROOT_CONFIG_FILE} at the repository root. ` +
            `It belongs in the workspace manifest ${WORKSPACE_MANIFEST_LABEL} — run \`wayai migrate\` to copy it there.`,
        ),
      );
      if (r.legacyRootFields.length > 0) {
        noticeOnce('legacy-fields', () =>
          console.warn(
            `Note: ${ROOT_CONFIG_FILE} contains deprecated field(s) ${r.legacyRootFields.join(', ')}. ` +
              'These are ignored — only organization_id is used now. You can remove them.',
          ),
        );
      }
      return;
    case 'invalid': {
      const emit = () => console.error(invalidMessage(r.file, r.detail));
      if (posture === 'hard') emit();
      else noticeOnce(`invalid:${r.source}`, emit);
      return;
    }
    case 'conflict':
      console.error(conflictMessage(r.manifestOrg, r.rootOrg));
      return;
    case 'none':
      if (posture === 'hard') console.error(missingMessage());
      return;
  }
}

/**
 * The organization binding plus which file supplied it. Returns null when no
 * file declares one — and also when one is declared unusably or the two
 * disagree, in which case the reason is printed to stderr first.
 */
export function resolveRepoConfig(root?: string): ResolvedRepoConfig | null {
  const gitRoot = root ?? findGitRoot();
  if (!gitRoot) return null;

  const r = resolve(gitRoot);
  report(r, 'soft');
  return r.kind === 'ok' ? r.resolved : null;
}

/**
 * The organization binding, or null. See `resolveRepoConfig` for the posture.
 *
 * **Null means "not usable", NOT "not declared"** — a disagreement and an
 * unparseable file both land here. Callers that merely REPORT the org (`status`,
 * `report-create`) can take that at face value; callers that ACT on one and
 * substitute a default when it is absent must NOT — see
 * `readRepoConfigUnlessBlocked`.
 */
export function readRepoConfig(root?: string): RepoConfig | null {
  return resolveRepoConfig(root)?.config ?? null;
}

/**
 * The organization binding for a caller that ACTS on it but tolerates its
 * absence: null when nothing is declared, THROWS when something is declared and
 * cannot be resolved.
 *
 * The distinction is the whole point. The Data surface falls back to the
 * backend's default organization when no org is selected, and
 * `resolveOrganizationId` falls back to a single-org auto-select — so handing
 * either the plain `null` that a DISAGREEMENT also produces makes it pick an
 * organization neither file named, push a teammate's config into it, and exit 0.
 * That is a worse outcome than the silent pick this design refuses, reached
 * through the one boundary where the verdict was being discarded.
 *
 * Throws rather than exiting so the refusal travels as a value; `expected`-tagged
 * because a repo carrying two config files is a user condition, not a WayAI bug,
 * and `shouldReportToSentry` must drop it.
 */
export function readRepoConfigUnlessBlocked(root?: string): RepoConfig | null {
  const gitRoot = root ?? findGitRoot();
  if (!gitRoot) return null;

  const r = resolve(gitRoot);
  switch (r.kind) {
    case 'ok':
      report(r, 'soft');
      return r.resolved.config;
    case 'none':
      return null;
    case 'conflict':
      throw expected(conflictMessage(r.manifestOrg, r.rootOrg));
    case 'invalid':
      throw expected(invalidMessage(r.file, r.detail));
  }
}

/**
 * The organization binding, or exit. The hard half of the posture split: every
 * failure names the file to fix and the command that fixes it.
 */
export function requireRepoConfig(root?: string): RepoConfig {
  const gitRoot = root ?? findGitRoot();
  const r: Resolution = gitRoot ? resolve(gitRoot) : { kind: 'none' };
  report(r, 'hard');
  if (r.kind === 'ok') return r.resolved.config;
  return process.exit(1);
}

/**
 * Why this repo has no usable organization, when the reason is something a user
 * must act on rather than simply "none declared".
 *
 * `wayai status` is the one caller. It reports `scoped: false` for every null
 * resolution, and without this it would tell a user whose two files disagree
 * that their repo is unscoped and to run `wayai init` — which rewrites the
 * manifest and cannot touch a disagreement that lives BETWEEN the files.
 */
export type RepoScopeBlocker =
  | { kind: 'conflict'; manifest_organization_id: string; root_organization_id: string }
  | { kind: 'invalid'; path: string; detail: string };

/**
 * The blocking condition, or null when the repo is simply unscoped (or scoped
 * fine). Silent by construction — `resolveRepoConfig` has already reported.
 */
export function readRepoScopeBlocker(root?: string): RepoScopeBlocker | null {
  const gitRoot = root ?? findGitRoot();
  if (!gitRoot) return null;
  const r = resolve(gitRoot);
  if (r.kind === 'conflict') {
    return {
      kind: 'conflict',
      manifest_organization_id: r.manifestOrg,
      root_organization_id: r.rootOrg,
    };
  }
  if (r.kind === 'invalid') return { kind: 'invalid', path: r.file, detail: r.detail };
  return null;
}

/** What `wayai migrate` should do about the org binding, and why. */
export type OrgMigrationPlan =
  | { kind: 'nothing' }
  | { kind: 'copy'; config: RepoConfig }
  | { kind: 'refuse'; message: string };

/**
 * Decide whether the root binding can be copied into the manifest.
 *
 * The decision lives HERE, with the rule it applies: migrate refusing a
 * different pair from the one every other command refuses would be worse than
 * either behaviour on its own. A broken or old-format ROOT file is deliberately
 * silent — `readRepoConfig` already explains it on every command, and migrate
 * has no better answer than "fix that file".
 */
export function planOrgMigration(gitRoot: string): OrgMigrationPlan {
  const manifest = declarationFrom(loadWorkspaceManifest(gitRoot));
  const root = declarationFrom(loadYamlMapping(rootConfigPath(gitRoot)));

  if (root.kind !== 'declared') return { kind: 'nothing' };

  if (manifest.kind === 'declared') {
    if (manifest.config.organization_id === root.config.organization_id) return { kind: 'nothing' };
    return {
      kind: 'refuse',
      message: conflictMessage(manifest.config.organization_id, root.config.organization_id, [
        'Reconcile them by hand — migrate cannot know which one you meant.',
      ]),
    };
  }
  if (manifest.kind === 'invalid') {
    return {
      kind: 'refuse',
      message:
        `Cannot copy the organization binding: ${WORKSPACE_MANIFEST_LABEL} is unusable — ${manifest.detail}. ` +
        'Fix or delete it, then re-run `wayai migrate`.',
    };
  }
  return { kind: 'copy', config: root.config };
}

/**
 * Write the organization into the workspace manifest, creating `wayai-ws/` if
 * needed.
 *
 * Read-modify-write, never a blind overwrite: the manifest also holds
 * `default_hub` / `default_base`, and re-binding the org must not delete a
 * teammate's routing default. For the same reason a manifest that does not parse
 * is refused rather than replaced — a broken parse is not a licence to clobber
 * whatever the file actually contained.
 *
 * **Refuses BEFORE writing** when the deprecated root file names a DIFFERENT
 * organization. The disagreement lives BETWEEN the two files, so writing the
 * manifest cannot resolve it — it creates it. Writing first and reporting after
 * would turn a repo that was working (scoped by the root fallback) into one
 * where every command exits 1, as a direct effect of the command that then
 * refused. Same rule `wayai migrate` follows two files over: validate
 * everything before mutating anything. The root file itself is never written.
 *
 * `organization_name` is replaced, never merged: the name belongs to the id, so
 * carrying a previous one forward would label the new organization with the old
 * one's name.
 *
 * Every refusal here is `expected`-tagged, the no-git-root one included: running
 * the command in the wrong directory is a user condition, and `shouldReportToSentry`
 * must drop it rather than file an autofix ticket per user, per run (gh #4174).
 *
 * YAML comments in an existing manifest do not survive the rewrite (js-yaml
 * dumps a plain document). Values do — including `default_hub` / `default_base`
 * and any key a future CLI added, which is why `lineWidth: -1` is set: a
 * preserved value must come back out spelled as it went in, not reflowed at
 * column 80. These are `YAML_DUMP_OPTIONS` (`yaml-writer.ts`) minus `noRefs`,
 * which needs a repeated object reference this document cannot contain; imported
 * rather than restated everywhere else, but `yaml-writer.ts` reaches back here
 * through `utils.ts` (which loads this module dynamically, at `utils.ts`'s
 * `readRepoConfigUnlessBlocked` call, for exactly that reason) and a static
 * import here would close the cycle. Two settings cost less than that.
 */
export function writeRepoConfig(config: RepoConfig, root?: string): string {
  const gitRoot = root ?? findGitRoot();
  if (!gitRoot) {
    throw expected('Not inside a git repository. Run `git init` first.');
  }

  // Pre-flight, in the order that matters: every refusal below happens before
  // the single write at the end. First: the manifest is read-modify-written, so a
  // symlinked one (live or dangling) is refused before it is read — otherwise its
  // target's keys would be copied into a real in-repo manifest.
  readRealFileOrThrow(gitRoot, workspaceManifestPath(gitRoot));
  const stale = declarationFrom(loadYamlMapping(rootConfigPath(gitRoot)));
  if (stale.kind === 'declared' && stale.config.organization_id !== config.organization_id) {
    throw expected(rebindRefusal(config.organization_id, stale.config.organization_id));
  }

  const load = loadWorkspaceManifest(gitRoot);
  if (load.kind === 'malformed') {
    throw expected(
      `Cannot write the organization binding: ${WORKSPACE_MANIFEST_LABEL} does not parse (${load.reason}). ` +
        'Fix or delete it, then re-run.',
    );
  }

  // Org fields first, then everything the file already carried. js-yaml dumps in
  // insertion order, so an unknown key a future CLI added survives untouched.
  const { organization_id: _id, organization_name: _name, ...rest } =
    load.kind === 'ok' ? load.doc : {};
  const doc: Record<string, unknown> = { organization_id: config.organization_id };
  if (config.organization_name?.trim()) doc.organization_name = config.organization_name.trim();
  Object.assign(doc, rest);

  const file = workspaceManifestPath(gitRoot);
  // `wayai-ws/` is realized as a real directory, never followed: `init`/`migrate` write the
  // binding here before any command resolves the layout.
  requireRealSubdirNoSymlink(gitRoot, path.dirname(file), true);
  writeFileNoFollow(gitRoot, file, Buffer.from(yaml.dump(doc, { lineWidth: -1, quotingType: '"', forceQuotes: false }), 'utf-8'));
  return file;
}
